/**
 * Feedback Loop Service
 *
 * Collects execution failure data from orchestration steps and builds
 * the diagnostic prompt and context needed to send to the AI generation
 * endpoint for automatic fix generation.
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5
 */

/** Maximum response body size included in failure data (5KB) */
const MAX_RESPONSE_BODY_SIZE = 5 * 1024;

/**
 * Check if any steps in the orchestration have failed.
 *
 * A step is considered failed if:
 * - Its execution status code is >= 400, OR
 * - It has a script error (assertion failure or runtime error)
 *
 * @param {Array} steps - Orchestration steps with execution results
 * @returns {boolean} True if at least one step has status >= 400 or script error
 */
export function hasFailedSteps(steps) {
  if (!Array.isArray(steps) || steps.length === 0) {
    return false;
  }

  return steps.some((step) => {
    const response = step.api_response;
    if (!response) return false;

    const statusCode =
      response.status_code ?? response.api_execution_code ?? null;

    // Check for HTTP error status
    if (statusCode !== null && statusCode >= 400) {
      return true;
    }

    // Check for script error
    if (step.script_error || step.scriptError) {
      return true;
    }

    return false;
  });
}

/**
 * Collect execution failure data from orchestration steps.
 *
 * Extracts detailed information about each failed step including
 * the error response data and the step's current configuration.
 * Response bodies are truncated to 5KB to avoid oversized payloads.
 *
 * @param {Array} steps - Orchestration steps with execution results
 * @returns {Array<FailedStepData>} Data for each failed step
 */
export function collectFailedStepData(steps) {
  if (!Array.isArray(steps) || steps.length === 0) {
    return [];
  }

  const failedSteps = [];

  for (const step of steps) {
    const response = step.api_response;
    if (!response) continue;

    const statusCode =
      response.status_code ?? response.api_execution_code ?? null;
    const scriptError = step.script_error || step.scriptError || null;

    const isFailed =
      (statusCode !== null && statusCode >= 400) || !!scriptError;

    if (!isFailed) continue;

    const apiRequest = step.api_request || {};

    // Truncate response body to 5KB
    let responseBody =
      response.response_body ?? response.body ?? '';
    if (typeof responseBody === 'object') {
      responseBody = JSON.stringify(responseBody);
    }
    if (responseBody && responseBody.length > MAX_RESPONSE_BODY_SIZE) {
      responseBody =
        responseBody.substring(0, MAX_RESPONSE_BODY_SIZE) + '... [truncated]';
    }

    // Extract response headers (normalize to object format)
    let responseHeaders = response.headers || {};
    if (Array.isArray(responseHeaders)) {
      responseHeaders = responseHeaders.reduce((obj, h) => {
        if (h && h.key) {
          obj[h.key] = h.value;
        }
        return obj;
      }, {});
    }

    // Extract current step configuration
    const currentConfig = {
      headers: apiRequest.headers || apiRequest.request_headers || [],
      body: getBodyString(apiRequest),
      bodyType: getBodyType(apiRequest),
      auth: apiRequest.authorization_type || apiRequest.authorization || { type: 'inherit' },
      preScript: extractScriptCode(apiRequest, 'prerequest'),
      postScript: extractScriptCode(apiRequest, 'postresponse'),
    };

    failedSteps.push({
      stepOrder: step.step_order,
      requestName:
        apiRequest.request_name ||
        apiRequest.name ||
        `Step ${step.step_order}`,
      statusCode: statusCode || 0,
      responseBody,
      responseHeaders,
      scriptError,
      currentConfig,
    });
  }

  return failedSteps;
}

/**
 * Build a diagnostic prompt describing the failures for the AI.
 *
 * Constructs a human-readable prompt that describes each failure
 * with its status code, error response, and any script errors.
 *
 * @param {Array<FailedStepData>} failedSteps - Collected failure data
 * @returns {string} System-generated prompt for AI
 */
export function buildDiagnosticPrompt(failedSteps) {
  if (!Array.isArray(failedSteps) || failedSteps.length === 0) {
    return 'No failures to fix — all steps passed.';
  }

  const lines = ['Fix failing steps in this orchestration.\n'];

  for (const step of failedSteps) {
    lines.push(
      `Step ${step.stepOrder} (${step.requestName}) failed:`
    );

    if (step.statusCode >= 400) {
      lines.push(`  - HTTP ${step.statusCode}`);
    }

    if (step.responseBody) {
      // Include a snippet of the response body (first 500 chars for the prompt)
      const snippet =
        step.responseBody.length > 500
          ? step.responseBody.substring(0, 500) + '...'
          : step.responseBody;
      lines.push(`  - Response: ${snippet}`);
    }

    if (step.scriptError) {
      lines.push(`  - Script error: ${step.scriptError}`);
    }

    lines.push('');
  }

  lines.push(
    'Analyze the errors above and generate corrective actions. ' +
      'For each fix, include a fix_reason explaining what error it addresses. ' +
      'Scope fixes only to the failed steps and their direct dependencies.'
  );

  return lines.join('\n');
}

/**
 * Build the generation request context including execution results.
 *
 * Creates a LivePageData-compatible context object that includes
 * the orchestration structure, failed step details with execution
 * results, and the project request list for reference.
 *
 * @param {Object} orchestration - Current orchestration object
 * @param {Array<FailedStepData>} failedSteps - Failure data
 * @param {Array} projectRequests - Project API request list
 * @returns {Object} LivePageData-compatible context object
 */
export function buildFixContext(orchestration, failedSteps, projectRequests) {
  const steps = orchestration?.steps || [];

  // Build step summaries including execution results
  const stepSummaries = steps.map((step) => {
    const apiRequest = step.api_request || {};
    const response = step.api_response;

    const summary = {
      step_order: step.step_order,
      request_name:
        apiRequest.request_name || apiRequest.name || `Step ${step.step_order}`,
      method: apiRequest.method_name || apiRequest.method || 'GET',
      url: apiRequest.raw_url || apiRequest.url || '',
    };

    // Include headers if present
    const headers = apiRequest.headers || apiRequest.request_headers || [];
    if (headers.length > 0) {
      summary.headers = headers;
    }

    // Include body if present
    const body = getBodyString(apiRequest);
    if (body) {
      summary.body = body;
    }

    // Include execution response if available
    if (response) {
      summary.response = {
        status_code:
          response.status_code ?? response.api_execution_code ?? null,
        body: response.response_body ?? response.body ?? null,
      };
    }

    return summary;
  });

  // Build the failed steps detail for the context
  const failedStepDetails = failedSteps.map((fs) => ({
    step_order: fs.stepOrder,
    request_name: fs.requestName,
    status_code: fs.statusCode,
    error_body: fs.responseBody,
    script_error: fs.scriptError,
    current_config: fs.currentConfig,
  }));

  return {
    orchestration: {
      id: orchestration?.orchestration_id || orchestration?.id,
      name: orchestration?.name || orchestration?.orchestration_name || '',
      total_steps: steps.length,
      steps: stepSummaries,
      failed_steps: failedStepDetails,
    },
    project: {
      requests: Array.isArray(projectRequests)
        ? projectRequests.map((r) => ({
            id: r.id,
            name: r.request_name || r.name || '',
            method: r.method_name || r.method || 'GET',
            url: r.raw_url || r.url || '',
          }))
        : [],
    },
  };
}

// ─── Internal Helpers ───────────────────────────────────────────────────────

/**
 * Extract body content as a string from an api_request object.
 *
 * @param {Object} apiRequest - The step's api_request object
 * @returns {string} Body string or empty string
 */
function getBodyString(apiRequest) {
  if (!apiRequest) return '';

  const body = apiRequest.body || apiRequest.request_body;
  if (!body) return '';

  if (typeof body === 'string') return body;
  if (body.body && typeof body.body === 'string') return body.body;
  if (body.value && typeof body.value === 'string') return body.value;
  if (typeof body === 'object') return JSON.stringify(body);

  return '';
}

/**
 * Extract body type from an api_request object.
 *
 * @param {Object} apiRequest - The step's api_request object
 * @returns {string} Body type string (e.g., 'raw', 'json', 'form-data')
 */
function getBodyType(apiRequest) {
  if (!apiRequest) return 'raw';

  const body = apiRequest.body || apiRequest.request_body;
  if (!body) return 'raw';

  if (body.type) return body.type;
  if (body.bodyType) return body.bodyType;

  return 'raw';
}

/**
 * Extract script code from an api_request's scripts array.
 *
 * @param {Object} apiRequest - The step's api_request object
 * @param {string} executionTime - 'prerequest' or 'postresponse'
 * @returns {string} Script code or empty string
 */
function extractScriptCode(apiRequest, executionTime) {
  const scripts = apiRequest?.scripts || [];
  const scriptObj = scripts.find(
    (s) => (s.executionTime || s.execution_time) === executionTime
  );

  if (!scriptObj) return '';

  const sourceCode = scriptObj.sourceCode || scriptObj.source_code;
  if (!sourceCode) return '';

  try {
    const parsed = JSON.parse(sourceCode);
    return parsed.code || '';
  } catch {
    return sourceCode || '';
  }
}
